import { ExclamationCircleOutlined, WarningOutlined } from "@ant-design/icons";
import { Alert } from "antd";
import type React from "react";
import type { errorObject } from "types/components/IAITagGenerationWizard";

interface ErrorAlertProps {
    activeError?: errorObject | null;
    lastErrorMessage?: string | null;
}

export const ErrorAlert: React.FC<ErrorAlertProps> = ({ activeError, lastErrorMessage }) => {
    const getAlertConfig = (errorObj: errorObject) => {
        const { message, code } = errorObj;
        let config = {
            type: "error" as "error" | "warning" | "info",
            icon: <ExclamationCircleOutlined />,
            title: "AI wizard Error",
            description: message as string | React.ReactNode,
            className: "mb-4 border-l-4 border-l-red-500 shadow-sm",
            action: null,
        };

        if (code === "INVALID_REQUEST" || message.toLowerCase().includes("unrelated") || message.toLowerCase().includes("off-topic")) {
            config = {
                ...config,
                type: "warning",
                icon: <WarningOutlined />,
                title: "Off-topic Prompt Detected",
                description: `${message}. Please modify your prompt to focus specifically on classification tag generation.`,
                className: "mb-4 border-l-4 border-l-yellow-500 shadow-sm",
            };
        }

        if (code === "INVALID_RESPONSE" || message.toLowerCase().includes("valid response")) {
            config = {
                ...config,
                type: "error",
                icon: <ExclamationCircleOutlined />,
                title: "Invalid Response",
                description:
                    "The AI failed to generate tags in the expected format or quantity. This might be due to an issue with the prompt or an internal AI error. Please review your prompt for clarity and constraints, or try again later.",
                className: "mb-4 border-l-4 border-l-red-500 shadow-sm",
            };
        }

        if (code === "UNIQUE_TAG_NAME_VALIDATION") {
            const tagNameMatch = message.match(/tag\s+['"]?([^'"]+)['"]?\s+already exists/i);
            const tagName = tagNameMatch ? tagNameMatch[1] : null;
            config = {
                ...config,
                title: "Duplicate Tag Name",
                description: tagName ? (
                    <>
                        The tag <strong>"{tagName}"</strong> already exists. Please remove or rename this tag before saving.
                    </>
                ) : (
                    <>One or more tags with the same name already exist. Please review your selected tags.</>
                ),
            };
        }

        if (code === "REQUEST_TIMEOUT" || message.toLowerCase().includes("timed out")) {
            config = {
                ...config,
                type: "error",
                icon: <ExclamationCircleOutlined />,
                title: "Request Timed Out",
                description: "The request took too long to complete. This might be due to network issues or high server load. Please try again later.",
                className: "mb-4 border-l-4 border-l-red-500 shadow-sm",
            };
        }

        return config;
    };

    if (lastErrorMessage && !activeError) {
        return (
            <Alert
                type="warning"
                showIcon
                icon={<WarningOutlined />}
                closable={false}
                title={<span className="font-medium">Previous Error</span>}
                description={<div className="text-sm">{lastErrorMessage}</div>}
                className="mb-4 border-l-4 border-l-yellow-500 shadow-sm"
            />
        );
    }

    if (activeError && activeError.message) {
        const config = getAlertConfig(activeError);
        return (
            <Alert
                type={config.type}
                showIcon
                closable={false}
                icon={config.icon}
                title={<span className="font-medium">{config.title}</span>}
                description={<div className="text-sm">{config.description}</div>}
                className={config.className}
                action={config.action}
            />
        );
    }

    return null;
};
